import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook to detect active speaker from remote audio streams
 * Uses Web Audio API to analyze audio levels
 */
export default function useActiveSpeaker(remoteStreams, options = {}) {
  const {
    threshold = 15,        // Minimum level to be considered speaking
    hysteresis = 500,      // ms to wait before switching speakers
    sampleInterval = 100,  // ms between samples
  } = options;

  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [speakerLevels, setSpeakerLevels] = useState({}); // userId -> level (0-100)
  
  const audioContextRef = useRef(null);
  const analysersRef = useRef(new Map()); // userId -> { analyser, source }
  const lastSpeakerTimeRef = useRef(0);
  const intervalRef = useRef(null);

  // Initialize audio context
  useEffect(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return () => {
      if (audioContextRef.current?.state !== 'closed') {
        audioContextRef.current?.close();
      }
    };
  }, []);

  // Set up analysers for each remote stream
  useEffect(() => {
    if (!audioContextRef.current || !remoteStreams) return;

    const ctx = audioContextRef.current;
    const currentAnalysers = analysersRef.current;

    // Add new streams
    remoteStreams.forEach((stream, odlerId) => {
      if (!currentAnalysers.has(odlerId)) {
        try {
          const audioTrack = stream.getAudioTracks()[0];
          if (!audioTrack) return;

          const source = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.5;
          source.connect(analyser);

          currentAnalysers.set(odlerId, { analyser, source });
        } catch (err) {
          console.warn('[useActiveSpeaker] Failed to create analyser for', odlerId, err);
        }
      }
    });

    // Remove old streams
    currentAnalysers.forEach((_, odlerId) => {
      if (!remoteStreams.has(odlerId)) {
        const entry = currentAnalysers.get(odlerId);
        entry?.source?.disconnect();
        currentAnalysers.delete(odlerId);
      }
    });
  }, [remoteStreams]);

  // Sample audio levels periodically
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      const levels = {};
      let maxLevel = 0;
      let maxUserId = null;

      analysersRef.current.forEach((entry, odlerId) => {
        const { analyser } = entry;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);

        // Calculate RMS level
        const sum = dataArray.reduce((acc, val) => acc + val, 0);
        const avg = sum / dataArray.length;
        const level = Math.min(100, avg * 1.5);

        levels[odlerId] = level;

        if (level > maxLevel && level > threshold) {
          maxLevel = level;
          maxUserId = odlerId;
        }
      });

      setSpeakerLevels(levels);

      // Apply hysteresis to prevent rapid switching
      const now = Date.now();
      if (maxUserId && (now - lastSpeakerTimeRef.current > hysteresis || maxUserId === activeSpeakerId)) {
        if (maxUserId !== activeSpeakerId) {
          setActiveSpeakerId(maxUserId);
          lastSpeakerTimeRef.current = now;
        }
      } else if (!maxUserId && now - lastSpeakerTimeRef.current > hysteresis * 2) {
        // No one speaking for a while
        setActiveSpeakerId(null);
      }
    }, sampleInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [threshold, hysteresis, sampleInterval, activeSpeakerId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      analysersRef.current.forEach((entry) => {
        entry?.source?.disconnect();
      });
      analysersRef.current.clear();
    };
  }, []);

  return { activeSpeakerId, speakerLevels };
}
